import { useState } from "react";
import { Seo } from "@/components/Seo";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MessageCircle, Phone, ShieldCheck, Plus } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";
import Marquee from "@/components/Marquee";
import Activities from "@/components/Activities";
import PartnersGrid from "@/components/PartnersGrid";
import BrandsGrid from "@/components/BrandsGrid";
import Testimonials from "@/components/Testimonials";
import BeforeAfterSection from "@/components/BeforeAfterSection";
import PromoBanner from "@/components/PromoBanner";
import NewsSection from "@/components/NewsSection";
import VehiclesSection from "@/components/vehicles/VehiclesSection";
import ExternalNewsBand from "@/components/ExternalNewsBand";
import { useSettings, mediaUrl } from "@/context/SettingsContext";

const Show = ({ k, children }) => { const { settings } = useSettings(); return settings.sections?.[k] === false ? null : children; };

const Hero = () => {
  const { t } = useLanguage();
  const { settings } = useSettings();
  const h = { ...t.hero, ...Object.fromEntries(Object.entries(settings.hero).filter(([, v]) => v)) };
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 20 });
  const sy = useSpring(my, { stiffness: 50, damping: 20 });
  const imgX = useTransform(sx, [-0.5, 0.5], [-18, 18]);
  const imgY = useTransform(sy, [-0.5, 0.5], [-12, 12]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      className="relative flex min-h-screen items-center overflow-hidden bg-white pt-28"
      onMouseMove={onMove}
      data-testid="hero-section"
    >
      <div className="dots-bg pointer-events-none absolute inset-0 opacity-70" />
      <div className="pointer-events-none absolute -left-40 top-20 h-[32rem] w-[32rem] rounded-full bg-mint blur-3xl" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-[28rem] w-[28rem] rounded-full bg-cream blur-3xl" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="mb-8 inline-flex max-w-full items-center gap-3 rounded-full border border-brand/30 bg-mint px-4 py-2"
          >
            <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-brand" />
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand sm:text-xs lg:whitespace-nowrap">
              {h.badge}
            </span>
          </motion.div>

          <h1 className="font-display max-w-6xl text-4xl font-bold leading-[1.08] tracking-tight text-forest sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.4}>{h.line1}</MaskedLine>
            <MaskedLine delay={0.55} className="text-gold">{h.line2}</MaskedLine>
            <MaskedLine delay={0.7} className="text-brand">{h.line3}</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-brand-hover sm:text-lg"
          >
            {h.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact?type=devis#formulaire"
              data-testid="hero-devis-cta-button"
              className="group flex items-center gap-3 rounded-full bg-gold px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-white shadow-xl shadow-gold/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover"
            >
              {t.hero.ctaQuote}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
            <Link
              to="/contact?type=devis#formulaire"
              data-testid="hero-whatsapp-cta-button"
              className="group flex items-center gap-3 rounded-full border-2 border-brand bg-white px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-brand transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand hover:text-white"
            >
              <MessageCircle className="h-4 w-4" />
              {t.hero.ctaWhats}
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-3"
          >
            {t.hero.chips.map((chip) => (
              <span
                key={chip}
                className="flex items-center gap-2.5 rounded-full border border-gold/30 bg-cream px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold"
              >
                <span className="h-1.5 w-1.5 rotate-45 bg-gold" />
                {chip}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative lg:col-span-4"
        >
          <div className="absolute inset-4 rotate-3 rounded-[2.5rem] bg-gold/15" />
          <div className="absolute inset-4 -rotate-3 rounded-[2.5rem] bg-brand/15" />
          <motion.div
            style={{ x: imgX, y: imgY }}
            className="relative rounded-[2.5rem] border border-brand/10 bg-white p-10 shadow-2xl shadow-brand/10 sm:p-14"
          >
            <img
              src={mediaUrl(settings.logo_full_url)}
              alt={`${settings.company_name} — ${settings.slogan}`}
              className="animate-float-slow w-full"
              data-testid="hero-logo-image"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const Credo = () => {
  const { t } = useLanguage();
  const { settings } = useSettings();
  const credo = settings.content?.credo || t.credo.text;
  return (
    <section className="relative py-28 sm:py-36" data-testid="credo-section">
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <Reveal>
          <p className="mb-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-gold">
            <span className="h-px w-8 bg-gold" />
            {t.credo.overline}
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="font-display text-2xl font-bold leading-snug text-forest sm:text-4xl lg:text-[2.75rem]">
            {credo}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const Stats = () => {
  const { t } = useLanguage();
  return (
    <section className="bg-brand" data-testid="stats-section">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px bg-white/15 lg:grid-cols-4">
        {t.stats.items.map((s, i) => (
          <Reveal key={s.l} delay={i * 0.1} className="bg-brand">
            <div className="flex flex-col items-center px-6 py-12 text-center transition-colors duration-500 hover:bg-brand-hover">
              <span className="font-display text-4xl font-extrabold text-white sm:text-5xl">
                {s.v}
              </span>
              <span className="mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white/80">
                {s.l}
              </span>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const ServicesPreview = () => {
  const { t } = useLanguage();
  const { settings } = useSettings();
  const items = t.services.items.map((x, i) => ({ ...x, ...(settings.content?.services?.[i] || {}) }));
  return (
    <section className="py-24 sm:py-32" data-testid="services-preview-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-16 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <SectionHead overline={t.servicesPreview.overline} title={t.servicesPreview.title} />
          <Reveal delay={0.2}>
            <Link
              to="/services"
              data-testid="services-preview-cta"
              className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold transition-colors hover:text-gold-hover"
            >
              {t.servicesPreview.cta}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.08}>
              <Link
                to="/services"
                data-testid={`manifesto-chapter-${s.num}`}
                className="group relative block h-full overflow-hidden rounded-3xl border border-brand/10 bg-white p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/40 hover:shadow-2xl hover:shadow-gold/10"
              >
                <span className="font-mono text-4xl font-light text-gold/50 transition-colors duration-500 group-hover:text-gold sm:text-5xl">
                  {s.num}
                </span>
                <h3 className="font-display mt-6 text-xl font-bold text-forest sm:text-2xl">
                  {s.title}
                </h3>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-brand">
                  {s.tagline}
                </p>
                <div className="mt-8 flex justify-end">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-mint text-brand transition-all duration-300 group-hover:bg-gold group-hover:text-white">
                    <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </span>
                </div>
                <span className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-brand to-gold transition-transform duration-500 group-hover:scale-x-100" />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Leadership = () => {
  const { t } = useLanguage();
  const { settings } = useSettings();
  const members = settings.content?.team?.length ? settings.content.team : t.leadership.members;
  return (
    <section className="bg-cream py-24 sm:py-32" data-testid="leadership-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead overline={t.leadership.overline} title={t.leadership.title} accent={t.leadership.titleAccent} />

        <div className={`mt-16 grid gap-6 md:grid-cols-2 ${members.length > 2 ? "lg:grid-cols-3" : ""} ${members.length === 4 ? "xl:grid-cols-4" : ""}`}>
          {members.map((m, i) => (
            <Reveal key={m.name} delay={i * 0.15}>
              <div className="group flex h-full flex-col rounded-3xl border border-gold/15 bg-white p-10 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-gold/10 sm:p-12">
                {m.photo_url ? (
                  <img src={mediaUrl(m.photo_url)} alt={m.name} className="mb-8 h-28 w-28 rounded-3xl object-cover shadow-lg" data-testid={`leader-photo-${i}`} />
                ) : (
                  <div className={`mb-8 flex h-20 w-20 items-center justify-center rounded-3xl text-white ${i % 2 === 0 ? "bg-gold" : "bg-brand"}`}>
                    <span className="font-display text-2xl font-extrabold">
                      {m.name.trim().split(/\s+/).map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                    </span>
                  </div>
                )}
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">
                  {m.role}
                </p>
                <h3 className="font-display mt-2 text-2xl font-bold text-forest sm:text-3xl">
                  {m.name}
                </h3>
                <p className="mt-5 max-w-md text-sm leading-relaxed text-brand-hover sm:text-base">
                  {m.bio}
                </p>
                {m.phone && (
                <a
                  href={`tel:${m.phone.replace(/\s/g, "")}`}
                  data-testid={`leader-phone-${i}`}
                  className="mt-6 inline-flex w-fit items-center gap-2.5 whitespace-nowrap rounded-full bg-mint px-3.5 py-2 font-mono text-[11px] tracking-[0.08em] text-brand transition-colors hover:bg-brand hover:text-white"
                >
                  <Phone className="h-3.5 w-3.5" />
                  {m.phone}
                </a>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Partners = () => {
  const { t } = useLanguage();
  return (
    <section className="py-24 sm:py-32" data-testid="partners-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead
          overline={t.partners.overline}
          title={t.partners.title}
          accent={t.partners.titleAccent}
          sub={t.partners.sub}
        />
        <PartnersGrid />
        <Reveal delay={0.2}>
          <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.2em] text-brand">
            {t.partners.legal}
          </p>
        </Reveal>
        <Show k="brands"><BrandsGrid label={t.partners.brandsLabel} /></Show>
      </div>
    </section>
  );
};

const Faq = () => {
  const { t } = useLanguage();
  const { settings } = useSettings();
  const faqItems = settings.content?.faq?.length ? settings.content.faq : t.faq.items;
  const [open, setOpen] = useState(0);
  return (
    <section className="py-24 sm:py-32" data-testid="faq-section">
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <SectionHead overline={t.faq.overline} title={t.faq.title} accent={t.faq.titleAccent} />

        <div className="mt-12 space-y-3">
          {faqItems.map((item, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div
                className={`rounded-2xl border transition-colors duration-300 ${
                  open === i ? "border-gold/40 bg-cream" : "border-brand/10 bg-white"
                }`}
              >
                <button
                  onClick={() => setOpen(open === i ? -1 : i)}
                  data-testid={`faq-question-${i}`}
                  className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className={`font-display text-base font-bold sm:text-lg ${open === i ? "text-gold" : "text-forest"}`}>
                    {item.q}
                  </span>
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all duration-300 ${
                      open === i ? "rotate-45 bg-gold text-white" : "bg-mint text-brand"
                    }`}
                  >
                    <Plus className="h-4 w-4" />
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="max-w-2xl px-6 pb-6 text-sm leading-relaxed text-brand-hover sm:text-base">
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const CtaBand = () => {
  const { t } = useLanguage();
  return (
    <section className="relative overflow-hidden bg-brand py-28" data-testid="cta-band-section">
      <div className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -bottom-24 -right-16 h-80 w-80 rounded-full bg-gold/30" />
      <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
        <Reveal>
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-5xl">
            {t.cta.title} <span className="text-gold">{t.cta.titleAccent}</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-5 text-base text-white/85 sm:text-lg">{t.cta.sub}</p>
        </Reveal>
        <Reveal delay={0.3}>
          <Link
            to="/contact?type=devis#formulaire"
            data-testid="cta-band-devis-button"
            className="group mt-10 inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-white shadow-xl shadow-forest/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover"
          >
            {t.cta.button}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const Home = () => (
  <main data-testid="home-page">
    <Seo page="home" />
    <Hero />
    <Show k="marquee"><Marquee /></Show>
    <PromoBanner />
    <Show k="credo"><Credo /></Show>
    <Show k="stats"><Stats /></Show>
    <Show k="news"><NewsSection /></Show>
    <Show k="news_external"><ExternalNewsBand /></Show>
    <Show k="activities"><Activities /></Show>
    <Show k="services"><ServicesPreview /></Show>
    <Show k="vehicles"><VehiclesSection /></Show>
    <Show k="before_after"><BeforeAfterSection preview /></Show>
    <Show k="leadership"><Leadership /></Show>
    <Show k="testimonials"><Testimonials /></Show>
    <Show k="partners"><Partners /></Show>
    <Show k="faq"><Faq /></Show>
    <Show k="cta"><CtaBand /></Show>
  </main>
);

export default Home;
